import { useState } from "react";
import { useAuth } from "@/lib/auth";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { LogIn, UserPlus } from "lucide-react";
import { toast } from "sonner";

function formatErr(detail) {
  if (!detail) return "Si è verificato un errore. Riprova.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  return String(detail);
}

export default function Login() {
  const { login, register } = useAuth();
  const [mode, setMode] = useState("login");
  const [nome, setNome] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!email || !password) { toast.error("Inserisci email e password"); return; }
    setBusy(true);
    try {
      if (mode === "login") await login(email, password);
      else await register({ nome, email, password });
    } catch (e) {
      toast.error(formatErr(e?.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-background">
      <div className="hidden lg:flex flex-col justify-between p-12 bg-slate-900 text-white relative overflow-hidden">
        <div className="grain absolute inset-0 opacity-40" />
        <div className="relative flex items-center gap-3">
          <img src="/logo-efferre.png" alt="EFFERRE Photo" className="h-11 w-auto object-contain bg-white/95 rounded-lg px-2 py-1" />
        </div>
        <div className="relative">
          <h1 className="text-4xl font-extrabold leading-tight">Il CRM che racconta<br />le vostre società.</h1>
          <p className="text-slate-400 mt-4 max-w-sm">Gestione collaboratori, pipeline SSD, portfolio e compensi — tutto in un unico posto.</p>
        </div>
        <div className="relative text-xs text-slate-500">© 2026 EFFERRE Photo</div>
      </div>

      <div className="flex items-center justify-center p-6">
        <Card className="w-full max-w-sm p-8 animate-fade-up">
          <div className="lg:hidden flex items-center gap-2.5 mb-6">
            <img src="/logo-efferre.png" alt="EFFERRE Photo" className="h-9 w-auto object-contain" />
          </div>
          <h2 className="text-2xl font-extrabold text-slate-900 dark:text-white">{mode === "login" ? "Accedi" : "Crea account collaboratore"}</h2>
          <p className="text-sm text-slate-500 mt-1 mb-6">{mode === "login" ? "Entra con le tue credenziali." : "Registrati come collaboratore."}</p>

          <div className="grid gap-3">
            {mode === "register" && (
              <div className="grid gap-1.5">
                <Label>Nome e cognome</Label>
                <Input data-testid="auth-nome-input" value={nome} onChange={(e) => setNome(e.target.value)} placeholder="Mario Rossi" />
              </div>
            )}
            <div className="grid gap-1.5">
              <Label>Email</Label>
              <Input data-testid="auth-email-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} onKeyDown={(e) => e.key === "Enter" && submit()} placeholder="nome@efferrephoto.it" />
            </div>
            <div className="grid gap-1.5">
              <Label>Password</Label>
              <Input data-testid="auth-password-input" type="password" value={password} onChange={(e) => setPassword(e.target.value)} onKeyDown={(e) => e.key === "Enter" && submit()} placeholder="••••••" />
            </div>
            <Button data-testid="auth-submit-button" onClick={submit} disabled={busy} className="mt-2 rounded-full gap-1.5">
              {mode === "login" ? <LogIn className="h-4 w-4" /> : <UserPlus className="h-4 w-4" />}
              {busy ? "Attendere…" : mode === "login" ? "Accedi" : "Registrati"}
            </Button>
          </div>

          <div className="text-center text-sm text-slate-500 mt-5">
            {mode === "login" ? (
              <button data-testid="auth-switch-register" onClick={() => setMode("register")} className="hover:text-primary">Sei un collaboratore? <b>Registrati</b></button>
            ) : (
              <button data-testid="auth-switch-login" onClick={() => setMode("login")} className="hover:text-primary">Hai già un account? <b>Accedi</b></button>
            )}
          </div>
        </Card>
      </div>
    </div>
  );
}
